import { useCallback, useEffect, useState } from "react";
import { BellRing, Play, Target } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { formatDate, formatTRY } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { AiOzetSettings } from "@/components/settings/AiOzetSettings";

function RunResult({ r }) {
  if (!r) return null;
  if (r.rows) return (
    <table className="erp-table text-[12px] mt-2" data-testid="automation-target-result"><thead><tr><th>Personel</th><th className="text-right">Aylık Hedef</th><th className="text-right">Beklenen</th><th className="text-right">Gerçekleşen</th><th className="text-right">%</th><th></th></tr></thead>
      <tbody>{r.rows.map((x) => <tr key={x.staff}><td>{x.staff}</td><td className="text-right font-mono">{formatTRY(x.target)}</td><td className="text-right font-mono">{formatTRY(x.expected)}</td>
        <td className="text-right font-mono">{formatTRY(x.actual)}</td><td className={`text-right font-mono ${x.behind ? "text-rose-600" : "text-emerald-700"}`}>%{x.pct}</td><td className="text-xs">{x.behind ? "Gerisinde" : "Yolunda"}</td></tr>)}</tbody></table>);
  return (
    <div className="text-[12px] mt-2 space-y-0.5" data-testid="automation-reminder-result">
      {r.sent.map((x) => <div key={x.rep}>✓ {x.rep}: {x.count} kalem · {formatTRY(x.total)}</div>)}
      {r.already_sent.length > 0 && <div className="text-slate-500">Bugün zaten gönderilmiş: {r.already_sent.join(", ")}</div>}
      {r.no_items.length > 0 && <div className="text-slate-500">Bugün kalemi olmayan: {r.no_items.join(", ")}</div>}
      <div className="text-slate-500">Yönetici özeti: {r.manager_summary ? "gönderildi" : "yeni özet yok"}</div>
    </div>
  );
}

export default function AutomationTab() {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [res, setRes] = useState({});
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/automation").then(({ data }) => { setD(data); setF(data.settings); }), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="panel h-40 animate-pulse" />;
  const save = async () => {
    try { await api.put("/automation/settings", { ...f, target_day: Number(f.target_day) }); toast.success("Otomasyon ayarları kaydedildi"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const run = async (job) => {
    setBusy(job);
    try { const { data } = await api.post(`/automation/run/${job}`); setRes({ ...res, [job]: data }); toast.success("Çalıştırıldı"); load(); } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };
  const edit = can("ayarlar", "edit");
  return (
    <div className="space-y-4" data-testid="automation-tab">
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="panel p-4" data-testid="automation-reminder-card">
          <h3 className="font-semibold flex items-center gap-2 mb-2"><BellRing className="h-4 w-4" />Tahsilat Hatırlatma (günlük)</h3>
          <p className="text-[12px] text-slate-500 mb-3">Her satış temsilcisine bugün vadesi gelen çek/senet, ödeme sözü (gecikenler dahil) ve fatura vadeleri bildirim olarak gider; yöneticilere özet. Gün/temsilci başına bir kez.</p>
          <div className="flex items-end gap-3">
            <label className="text-[12px] text-slate-600">Saat (Europe/Istanbul)<Input type="time" value={f.reminder_time} disabled={!edit} onChange={(e) => setF({ ...f, reminder_time: e.target.value })} data-testid="automation-reminder-time" /></label>
            <label className="flex items-center gap-2 text-[13px] pb-2"><Switch checked={f.reminder_enabled} disabled={!edit} onCheckedChange={(v) => setF({ ...f, reminder_enabled: v })} data-testid="automation-reminder-enabled" />Aktif</label>
            {d.is_super && <Button size="sm" variant="outline" className="ml-auto" disabled={busy === "tahsilat"} onClick={() => run("tahsilat")} data-testid="automation-run-tahsilat"><Play className="h-3.5 w-3.5 mr-1" />Şimdi Çalıştır</Button>}
          </div>
          <RunResult r={res.tahsilat} />
        </div>
        <div className="panel p-4" data-testid="automation-target-card">
          <h3 className="font-semibold flex items-center gap-2 mb-2"><Target className="h-4 w-4" />Hedef Uyarıları (aylık)</h3>
          <p className="text-[12px] text-slate-500 mb-3">Beklenen = aylık hedef × (geçen gün / ayın gün sayısı). Faturalaşmış satışlar (iadeler düşülerek) beklenenin altındaysa yöneticilere bildirim. Ay/personel başına bir kez.</p>
          <div className="flex items-end gap-3">
            <label className="text-[12px] text-slate-600">Ayın günü<Input type="number" min="1" max="28" value={f.target_day} disabled={!edit} onChange={(e) => setF({ ...f, target_day: e.target.value })} data-testid="automation-target-day" /></label>
            <label className="flex items-center gap-2 text-[13px] pb-2"><Switch checked={f.target_enabled} disabled={!edit} onCheckedChange={(v) => setF({ ...f, target_enabled: v })} data-testid="automation-target-enabled" />Aktif</label>
            {d.is_super && <Button size="sm" variant="outline" className="ml-auto" disabled={busy === "hedef"} onClick={() => run("hedef")} data-testid="automation-run-hedef"><Play className="h-3.5 w-3.5 mr-1" />Şimdi Çalıştır</Button>}
          </div>
          <RunResult r={res.hedef} />
        </div>
      </div>
      {edit && <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="automation-save">Kaydet</Button>}
      <AiOzetSettings edit={edit} />
      <div className="panel p-4" data-testid="automation-logs">
        <div className="font-semibold text-[13px] mb-2">Son Çalışmalar</div>
        {d.logs.length === 0 ? <div className="text-[12px] text-slate-400">Henüz çalışma yok</div> : d.logs.map((l, i) => (
          <div key={i} className="text-[12px] text-slate-600 border-b border-slate-100 py-1">{formatDate(l.at)} · {{ tahsilat: "Tahsilat hatırlatma", hedef: "Hedef uyarısı", ai_gunluk: "AI Günlük Özet", ai_vade: "AI Vade Raporu" }[l.job] || l.job} · {l.by} · {l.note}</div>))}
      </div>
    </div>
  );
}
